// https://github.paypal.com/pages/Tracking-R/client-side-instrumentation/#record_ac
import template from 'string-placeholder';
import _isEmpty from 'lodash/isEmpty';
import _isString from 'lodash/isString';
import poll from '../../../js/lib/poll';
import Page from '../../../js/lib/page';
import { isC2MerchantMigrated } from '../../../js/lib/globalHelper';
import { PRODUCT, SPACE } from '../../../../constants/fptiConstants';

const maskRegEx =
  /(\d\d+|(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,}))/g; // eslint-disable-line
const maskPattern = '#########';

export const generateLinkData = (link, options) => {
  if (!_isEmpty(options)) {
    return template(link, options, { clean: true });
  }
  return link;
};

// Validate if analytics object is available on window.
const validateAnalyticsObject = () => {
  if (!(window.PAYPAL && window.PAYPAL.analytics)) {
    return Promise.reject();
  }
  return Promise.resolve();
};

/**
 *  Function to poll and check if analytics is available on window. If available fire FPTI.
 * @param options
 * @param callback
 * @returns {Promise<never>}
 */
export const trackingHelper = (options, callback) =>
  poll(validateAnalyticsObject, 4, 500)
    .then(() => {
      callback(options);
    })
    .catch(() => {});

// Get RSTA Value
const getRSTA = () => {
  return `${Page.getLocale().replace('-', '_')}`;
};

const rstaForC2Merchant = (existingCountyLocale) => {
  // if user is C2 merchant migrated change the country/locale else return existing country/locale
  if (isC2MerchantMigrated()) {
    const locale = Page.getLanguage() === 'en' ? 'en_US' : 'zh_CN';

    return `${locale}`;
  }

  return existingCountyLocale;
};

export function dataMasking(data) {
  if (_isString(data)) {
    return data.replace(maskRegEx, maskPattern);
  }

  return data;
}

// Get FPTI Options
const getFPTIOptions = (fptiOptions) => {
  const FPTIObject = (window && window.fpti) || {};
  // clearing out error props in case they exist
  FPTIObject.erpg = null;
  FPTIObject.erfd = null;
  FPTIObject.eccd = null;

  // FPTI options passed through props
  if (
    fptiOptions &&
    Object.prototype.hasOwnProperty.call(fptiOptions, 'page') &&
    Object.prototype.hasOwnProperty.call(fptiOptions, 'pgrp')
  ) {
    FPTIObject.page = fptiOptions.page;
    FPTIObject.pgrp = fptiOptions.pgrp;
  }

  // For setting link
  if (fptiOptions.link) {
    FPTIObject.link = fptiOptions.link;
    FPTIObject.pgln = `${fptiOptions.page}|${fptiOptions.link}`;
    FPTIObject.pglk = `${fptiOptions.pgrp}|${fptiOptions.link}`;
  }

  // Add event name to FPTI object
  FPTIObject.event_name = fptiOptions.event_name ? fptiOptions.event_name : null;

  // set component name
  FPTIObject.comp = 'smarthelpnodeweb';

  // Set event Type
  FPTIObject.e = null;
  if (fptiOptions.e) {
    FPTIObject.e = fptiOptions.e;
  }

  // set article doc id
  FPTIObject.article_doc_id = null;
  if (fptiOptions.article_doc_id) {
    FPTIObject.article_doc_id = fptiOptions.article_doc_id;
  }

  // Set RSTA
  const existingCountyLocale = window.fpti.rsta || getRSTA();
  FPTIObject.rsta = rstaForC2Merchant(existingCountyLocale);

  // add hpid if it is part of the options, else clear it
  FPTIObject.hpid = fptiOptions.hpid ? fptiOptions.hpid : null;

  // add help_doc_id and help_component_ids if available in the options, else clear them
  FPTIObject.help_doc_id = fptiOptions.help_doc_id || '';
  FPTIObject.help_component_ids = fptiOptions.help_component_ids || '';

  // Search keyword
  FPTIObject.srch = fptiOptions.srch ? dataMasking(fptiOptions.srch) : null;

  // For Omnichannel added new FPTI keys
  FPTIObject.ocp_context_metadata = fptiOptions.ocp_context_metadata ? fptiOptions.ocp_context_metadata : null;

  FPTIObject.t = fptiOptions.timestamp ? fptiOptions.timestamp : null;

  FPTIObject.context_status = fptiOptions.context_status ? fptiOptions.context_status : null;

  // customer confirmed
  FPTIObject.link_name = fptiOptions.link_name;

  FPTIObject.channel_name = fptiOptions.channel_name ? fptiOptions.channel_name : null;

  FPTIObject.rpa = fptiOptions.rpa ? fptiOptions.rpa : null;

  // Set product and space tags for tying events to Help Center IQ dashboard
  FPTIObject.product = PRODUCT;
  FPTIObject.space_key = SPACE;

  // set hc event info
  FPTIObject.hc_event_info = fptiOptions.hc_event_info ? fptiOptions.hc_event_info : null;

  // set kyc holds specific params if present
  FPTIObject.entry_point = fptiOptions.entry_point || null;
  FPTIObject.return_url = fptiOptions.return_url || null;
  FPTIObject.hold_status = fptiOptions.hold_status || null;

  return FPTIObject;
};

// Track Impression event
export const trackImpression = (params) =>
  trackingHelper(params, (options) => {
    const fptiData = getFPTIOptions(options);
    return window.PAYPAL.analytics.Analytics.prototype.recordImpression({ data: fptiData });
  });

// Track Activity Event
export const trackActivityEvent = (params) =>
  trackingHelper(params, (options) => {
    const fptiData = getFPTIOptions(options);
    window.PAYPAL.analytics.Analytics.prototype.recordEvent(fptiData.event_name, { data: fptiData });
  });

// Track Click Event
export const trackClickEvent = (params) =>
  trackingHelper(params, (options) => {
    const fptiData = getFPTIOptions(options);
    window.PAYPAL.analytics.Analytics.prototype.recordEvent(fptiData.event_name, { data: fptiData });
  });

// Track Javascript Error
// https://github.paypal.com/pages/Tracking-R/client-side-instrumentation/#record_error
export const trackJSError = (errorObject) => {
  if (window.PAYPAL && window.PAYPAL.trackJSError) {
    window.PAYPAL.trackJSError(errorObject);
  }
  // PAYPAL.analytics.logJSError(errorObject, ['TopicTree', ['errorType']]);
};
